import { Loader2, Package, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import leaveApi from '../LeaveManagement/leaveApi';
import { Card } from '../ui';
import { inventoryApi } from './inventoryApi.js';
import { getAllVehicles, getSites } from './inventoryOnboardingApi.js';

const InventoryDataLogsTable = () => {
  // Log screen view
  useScreenLogging('InventoryDataLogsTable', {
    component: 'InventoryDataLogsTable',
    screenType: 'inventory-data-logs',
  });

  const { itemId } = useParams();

  const [inventoryLogs, setInventoryLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [lastKey, setLastKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Cache for entity names to avoid repeated API calls
  const [entityNameCache, setEntityNameCache] = useState({});
  const [loadingEntities, setLoadingEntities] = useState(new Set());

  useEffect(() => {
    if (itemId) {
      fetchInventoryLedger();
    }
  }, [itemId]);

  const fetchInventoryLedger = async (loadMore = false) => {
    try {
      if (!loadMore) {
        setIsLoading(true);
        setErrorMessage(null);
      } else {
        setIsLoadingMore(true);
      }

      const result = await inventoryApi.getInventoryLedger({
        itemId: itemId,
        limit: 20,
        lastKey: loadMore ? lastKey : null,
      });

      if (result.success) {
        if (loadMore) {
          setInventoryLogs((prev) => [...prev, ...(result.entries || [])]);
        } else {
          setInventoryLogs(result.entries || []);
        }
        setHasMore(result.has_more || false);
        setLastKey(result.next_page_token || null);
        setIsLoading(false);
        setIsLoadingMore(false);

        // Preload entity names for better user experience
        if (result.entries && result.entries.length > 0) {
          preloadEntityNames(result.entries);
        }
      } else {
        setErrorMessage(result.message || 'Failed to fetch inventory logs');
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    } catch (error) {
      setErrorMessage(error.message || 'Failed to fetch inventory logs');
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMore = async () => {
    if (hasMore && !isLoadingMore && !isLoading) {
      await fetchInventoryLedger(true);
    }
  };

  // Fetch entity name using the lookup API
  const fetchEntityName = async (entityType, entityId) => {
    const cacheKey = `${entityType}_${entityId}`;

    // Return cached result if available
    if (entityNameCache[cacheKey]) {
      return entityNameCache[cacheKey];
    }

    // Avoid duplicate requests for the same entity
    if (loadingEntities.has(cacheKey)) {
      return null;
    }

    setLoadingEntities((prev) => new Set([...prev, cacheKey]));

    try {
      let displayName = null;

      // Call appropriate lookup method based on entity type
      switch (entityType.toLowerCase()) {
        case 'user':
        case 'employee':
          try {
            const userResult = await leaveApi.getUserInfo(entityId);
            if (userResult && userResult.user_name) {
              displayName = userResult.user_name;
            }
          } catch (e) {
            console.error('Error fetching user:', e);
          }
          break;
        case 'site':
          try {
            const sites = await getSites();
            const site = sites.find((s) => s.site_id === entityId);
            if (site && site.site_name) {
              displayName = site.site_name;
            }
          } catch (e) {
            console.error('Error fetching site:', e);
          }
          break;
        case 'vehicle':
          try {
            const vehicles = await getAllVehicles();
            const vehicle = vehicles.find((v) => v.vehicle_id === entityId);
            if (vehicle && vehicle.registration_no) {
              displayName = vehicle.registration_no;
            }
          } catch (e) {
            console.error('Error fetching vehicle:', e);
          }
          break;
        default:
          displayName = entityId;
      }

      // Cache the result
      const finalName = displayName || entityId;
      setEntityNameCache((prev) => ({
        ...prev,
        [cacheKey]: finalName,
      }));

      // Trigger UI update
      setLoadingEntities((prev) => {
        const newSet = new Set(prev);
        newSet.delete(cacheKey);
        return newSet;
      });

      return finalName;
    } catch (error) {
      console.error(
        `Error fetching entity name for ${entityType} ${entityId}:`,
        error
      );
      // Cache the ID as fallback
      const fallbackName = entityId;
      setEntityNameCache((prev) => ({
        ...prev,
        [cacheKey]: fallbackName,
      }));
      setLoadingEntities((prev) => {
        const newSet = new Set(prev);
        newSet.delete(cacheKey);
        return newSet;
      });
      return fallbackName;
    }
  };

  // Get cached entity name or return ID as fallback
  const getEntityDisplayName = (entityType, entityId) => {
    const cacheKey = `${entityType}_${entityId}`;
    return entityNameCache[cacheKey] || entityId;
  };

  // Preload entity names for all unique entities in the logs
  const preloadEntityNames = async (logs) => {
    const entitiesToLoad = new Set();

    for (const log of logs) {
      const fromType = log.from_type?.toString();
      const fromId = log.from_id?.toString();
      const toType = log.to_type?.toString();
      const toId = log.to_id?.toString();

      if (fromType && fromId) {
        const cacheKey = `${fromType}_${fromId}`;
        if (!entityNameCache[cacheKey] && !loadingEntities.has(cacheKey)) {
          entitiesToLoad.add(JSON.stringify({ type: fromType, id: fromId }));
        }
      }

      if (toType && toId) {
        const cacheKey = `${toType}_${toId}`;
        if (!entityNameCache[cacheKey] && !loadingEntities.has(cacheKey)) {
          entitiesToLoad.add(JSON.stringify({ type: toType, id: toId }));
        }
      }
    }

    // Load all entities in parallel
    const futures = Array.from(entitiesToLoad).map((entityStr) => {
      const entity = JSON.parse(entityStr);
      return fetchEntityName(entity.type, entity.id);
    });

    await Promise.all(futures);
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (e) {
      return timestamp;
    }
  };

  const formatLocation = (type, id) => {
    const cacheKey = `${type}_${id}`;
    const displayName = getEntityDisplayName(type, id);
    const isLoading = loadingEntities.has(cacheKey);

    if (isLoading) {
      return `${type}: Loading...`;
    }

    return `${type}: ${displayName}`;
  };

  if (isLoading && inventoryLogs.length === 0) {
    return (
      <div className="w-full h-full p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1
              className={typography.h1}
              style={{ color: '#000000', marginBottom: '0.5rem' }}
            >
              View Logs
            </h1>
          </div>
          <Card.Light className="p-12">
            <div className="text-center">
              <Loader2 className="w-16 h-16 mx-auto mb-4 text-blue-400 animate-spin" />
              <p className={typography.p} style={{ color: '#6B7280' }}>
                Loading inventory logs...
              </p>
            </div>
          </Card.Light>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="w-full h-full">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1
              className={typography.h1}
              style={{ color: '#000000', marginBottom: '0.5rem' }}
            >
              View Logs
            </h1>
          </div>
          <Card.Light className="p-12">
            <div className="text-center">
              <X className="w-16 h-16 mx-auto mb-4 text-red-400" />
              <h3
                className={typography.h3}
                style={{ color: '#DC2626', marginBottom: '0.5rem' }}
              >
                Error loading inventory logs
              </h3>
              <p className={typography.p} style={{ color: '#DC2626' }}>
                {errorMessage}
              </p>
              <button
                onClick={() => fetchInventoryLedger()}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          </Card.Light>
        </div>
      </div>
    );
  }

  if (inventoryLogs.length === 0) {
    return (
      <div className="w-full h-full">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1
              className={typography.h1}
              style={{ color: '#000000', marginBottom: '0.5rem' }}
            >
              View Logs
            </h1>
          </div>
          <Card.Light className="p-12">
            <div className="text-center">
              <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3
                className={typography.h3}
                style={{ color: '#6B7280', marginBottom: '0.5rem' }}
              >
                No transaction logs found
              </h3>
              <p className={typography.p} style={{ color: '#6B7280' }}>
                This inventory item has no transaction history yet.
              </p>
            </div>
          </Card.Light>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1
            className={typography.h1}
            style={{ color: '#000000', marginBottom: '0.5rem' }}
          >
            View Logs
          </h1>
        </div>

        <div className="overflow-x-auto">
          <Card.Light className="p-0 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b-2 border-gray-300">
                  <th className="px-4 py-3 text-left font-bold text-gray-900 border-r border-gray-300">
                    S.No
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-gray-900 border-r border-gray-300">
                    User Name
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-gray-900 border-r border-gray-300">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-gray-900 border-r border-gray-300">
                    From Source
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-gray-900 border-r border-gray-300">
                    To Source
                  </th>
                  <th className="px-4 py-3 text-left font-bold text-gray-900">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody>
                {inventoryLogs.map((log, index) => (
                  <tr
                    key={index}
                    className={`border-b border-gray-200 ${
                      index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                    }`}
                  >
                    <td className="px-4 py-3 border-r border-gray-200">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 border-r border-gray-200">
                      {log.user_name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 border-r border-gray-200">
                      {formatTimestamp(log.timestamp || '')}
                    </td>
                    <td className="px-4 py-3 border-r border-gray-200">
                      {formatLocation(
                        log.from_type || 'N/A',
                        log.from_id || 'N/A'
                      )}
                    </td>
                    <td className="px-4 py-3 border-r border-gray-200">
                      {formatLocation(
                        log.to_type || 'N/A',
                        log.to_id || 'N/A'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="max-w-[200px]">
                        <p
                          className="text-sm truncate"
                          title={log.notes || 'No notes'}
                        >
                          {log.notes || 'No notes'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card.Light>
        </div>

        {hasMore && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={loadMore}
              disabled={isLoadingMore}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoadingMore ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryDataLogsTable;

